import { AddCircle, DeleteForever, Warning } from '@mui/icons-material';
import { Box, Grid, Skeleton, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  CompanyStatus,
  OrderTypes,
  ProcessStep,
} from '../../../common/enums/enums';
import GenButton from '../../GenButton';
import GenDataText from '../../GenDataText';
import { GenConfirmationDialog } from '../../GenDialogs';
import GenSkeleton from '../../GenSkeleton';
import GenSnackBar from '../../GenSnackBar';
import GenTables from '../../GenTables';
import SectionTitle from '../../SectionTitle';
import GlobalColumns from './GlobalColumns';
import { AssigneeHandler, PhotosAndNotesButtons } from './WOrderReconServices';

const Observation = ({ serviceName, willBeAutoApproved }) => {
  const { t } = useTranslation('workOrdersTable');

  const subTitle =
    (willBeAutoApproved && (
      <Trans
        t={t}
        i18nKey={'detail.servicesTable.servicesDeletePopUp.subTitleAutoApprove'}
      />
    )) ||
    t('detail.servicesTable.servicesDeletePopUp.subTitle');

  return (
    <Grid container direction={'column'} alignItems="center">
      <Grid item width={'100%'}>
        <Typography
          variant="body1"
          color="textSecondary"
          textAlign="center"
          mb={2}
        >
          {subTitle}
        </Typography>
      </Grid>
      <Grid item width={'100%'}>
        <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
          <Typography variant="overline" color="textSecondary">
            {t('detail.servicesTable.servicesDeletePopUp.remove')}
          </Typography>
          <Typography
            variant="subtitle2"
            color="textPrimary"
            marginLeft={'7.5px'}
          >
            {serviceName}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
};

const DeleteServiceButton = ({
  text,
  deleteServicesFunction,
  vendorOrderId,
  apiIsLoading,
  autoApprove,
  services,
}) => {
  const { t } = useTranslation('workOrdersTable');

  const [open, setOpen] = useState(false);
  const [closedExecute, setClosedExecute] = useState(false);
  const [openSnack, setOpenSnack] = useState(false);

  useEffect(() => {
    if (closedExecute && !apiIsLoading) {
      setOpen(false);
      setClosedExecute(false);
    }
  }, [closedExecute, apiIsLoading]);

  const handleOpenClick = useCallback(() => {
    if (services.length > 1) {
      setOpen(true);
    } else {
      setOpenSnack(true);
    }
  }, [services.length]);

  const handleCloseClick = useCallback(() => {
    setClosedExecute(true);
  }, []);

  const handleOnConfirm = id => {
    deleteServicesFunction({ vendorOrderId, serviceId: id });
    handleCloseClick();
  };

  const willBeAutoApproved =
    autoApprove &&
    services
      .filter(service => service.serviceId !== text[0])
      .every(service => !!service.approver);

  const title =
    (willBeAutoApproved &&
      t('detail.servicesTable.servicesDeletePopUp.titleAutoApprove')) ||
    t('detail.servicesTable.servicesDeletePopUp.title');

  return (
    <>
      <GenButton
        iconForIconButton={<DeleteForever color="action" />}
        onClick={handleOpenClick}
        sx={{ padding: 0 }}
      />
      <GenConfirmationDialog
        open={open}
        title={title}
        titleIcon={Warning}
        titleIconColor="warning"
        onClose={handleCloseClick}
        closeText={t('detail.servicesTable.servicesDeletePopUp.cancel')}
        confirmText={t('detail.servicesTable.servicesDeletePopUp.send')}
        onConfirm={() => handleOnConfirm(text[0])}
        onRefuse={handleCloseClick}
        isLoading={apiIsLoading}
        children={
          <Observation
            serviceName={text[1]}
            willBeAutoApproved={willBeAutoApproved}
          />
        }
      />
      <GenSnackBar
        open={openSnack}
        message={t('detail.servicesTable.lastServiceError.message')}
        type="error"
        title={t('detail.servicesTable.lastServiceError.title')}
        onClose={() => setOpenSnack(false)}
      />
    </>
  );
};

export const Columns = (
  t,
  deleteServicesFunction,
  checkEditState,
  vendorOrderId,
  apiIsLoading,
  autoApprove,
  services,
  step,
  orderType = OrderTypes.WORK_ORDER,
  assignTech = false,
  assignTechCallback = () => {},
  isDealer = false,
  isVendorManager = false
) => {
  const showPhotosNotes = orderType === OrderTypes.SERVICE_ORDER;
  const globalColumns = GlobalColumns(t).filter(c => c.id !== 'price');

  return [
    {
      id: 'name',
      label: t('detail.servicesTable.servicesColumnLabels.nameLabel'),
      labelComponent: (
        <GenDataText textVariant="body1" textColor="textPrimary" />
      ),
      component: <GenDataText textVariant="body2" textColor="textPrimary" />,
      dataSource:
        orderType === OrderTypes.WORK_ORDER
          ? ['name']
          : ['quantity', 'x ', 'name'],
      align: 'inherit',
    },
    {
      id: 'amount',
      label: t('detail.servicesTable.servicesColumnLabels.amountLabel'),
      labelComponent: (
        <GenDataText textVariant="body1" textColor="textPrimary" />
      ),
      component: (
        <GenDataText
          textVariant="body2"
          textColor="textPrimary"
          currency={true}
        />
      ),
      dataSource: ['amount'],
      align: 'inherit',
      sx: { display: { xs: 'none', sm: 'table-cell' } },
    },
    ...globalColumns,
    ...(assignTech
      ? [
          {
            id: 'assignee',
            label: t('detail.reconServices.columns.assignTo'),
            labelComponent: (
              <GenDataText textVariant="body1" textColor="textPrimary" />
            ),
            component: (
              <AssigneeHandler
                assignTech={assignTechCallback}
                isDealer={isDealer}
                step={step}
                orderType={orderType}
                isVendorManager={isVendorManager}
              />
            ),
            dataSource: [
              [
                'declinedAt',
                'assignee',
                'serviceId',
                'vendorOrderServiceId',
                'assignedTechnicians',
              ],
            ],
            defaultValue: ' ',
            align: 'left',
            sx: { width: '185px' },
          },
        ]
      : []),
    ...(showPhotosNotes
      ? [
          {
            id: 'photos',
            label: '',
            labelComponent: '',
            component: <PhotosAndNotesButtons type={'photos'} />,
            dataSource: [['attachments', 'notes']],
            sx: { width: '10px', px: 0 },
          },
        ]
      : []),
    ...(showPhotosNotes
      ? [
          {
            id: 'notes',
            label: '',
            labelComponent: '',
            component: <PhotosAndNotesButtons type={'notes'} />,
            dataSource: [['attachments', 'notes']],
            sx: { width: '10px', pl: 0 },
          },
        ]
      : []),
    {
      id: 'remove',
      label: ' ',
      labelComponent: (
        <GenDataText textVariant="body1" textColor="textPrimary" />
      ),
      component: (
        <DeleteServiceButton
          deleteServicesFunction={deleteServicesFunction}
          vendorOrderId={vendorOrderId}
          apiIsLoading={apiIsLoading}
          autoApprove={autoApprove}
          services={services}
        />
      ),
      dataSource: [['serviceId', 'name']],
      align: 'right',
      sx: !checkEditState() && { display: { xs: 'none' } },
    },
  ];
};

const WOrderServices = ({
  data,
  isLoading,
  edit = true,
  autoApprove = false,
  deleteServicesFunction = undefined,
  apiIsLoading,
  onAddServiceClick,
  orderType = OrderTypes.WORK_ORDER,
  assignTech = false,
  assignTechCallback = () => {},
  isDealer = false,
  isVendorManager = false,
}) => {
  const { t } = useTranslation('workOrdersTable');

  const checkWOStep = () =>
    !(data?.step === ProcessStep.DONE || data?.step === ProcessStep.CANCELED);
  const checkEditState = () => (edit ? checkWOStep() : false);

  const requiresApprovalAndAutoApprove = data?.requiresApproval && autoApprove;
  const columns = Columns(
    t,
    deleteServicesFunction,
    checkEditState,
    data.vendorOrderId,
    apiIsLoading,
    requiresApprovalAndAutoApprove,
    data.services,
    data?.step,
    orderType,
    assignTech,
    assignTechCallback,
    isDealer,
    isVendorManager
  );

  const shouldEnableButtons =
    data?.vendor?.companyStatus === CompanyStatus.ACTIVE &&
    data?.dealer?.companyStatus === CompanyStatus.ACTIVE &&
    data?.vendor?.active !== false &&
    data?.dealer?.active !== false;

  return !isLoading ? (
    <Box
      sx={{
        bgcolor: 'background.paper',
        p: { xs: 2, lg: 4 },
        borderTop: '1px solid',
        borderColor: theme => theme.palette.divider,
      }}
    >
      <Grid container flexDirection="column" spacing={0.5}>
        <Grid item>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <SectionTitle
                title={t('detail.servicesTable.services')}
                subsection={true}
              />
            </Grid>
            <Grid item>
              {checkEditState() && (
                <GenButton
                  text={t('detail.servicesTable.addService')}
                  disabled={!shouldEnableButtons}
                  startIcon={<AddCircle />}
                  onClick={onAddServiceClick}
                />
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <GenTables
            tableHeadSx={{ backgroundColor: 'grey.50' }}
            data={data.services}
            columns={columns}
          />
        </Grid>
      </Grid>
    </Box>
  ) : (
    <Box
      data-testid="skeleton"
      sx={{
        bgcolor: 'background.paper',
        p: { xs: 2, lg: 4 },
        borderTop: '1px solid',
        borderColor: theme => theme.palette.divider,
      }}
    >
      <Grid container flexDirection="column" spacing={2}>
        <Grid item>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Skeleton variant="text" width={120} />
            </Grid>
            <Grid item>
              {edit && <Skeleton variant="rounded" width={180} height={40} />}
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <GenSkeleton sx={{ height: '290px' }} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default WOrderServices;
